import CartList from "#/components/cart/cartList";
import EmptyView from "#/components/cart/emptyView";
import { auth, cart } from "#/utils";
import { useLoading } from "#/utils/context/loading";
import IRRtoIRT from "#/utils/currency/IRRtoIRT";
import { Button, Divider, useBreakLG } from "@monta/design-system";
import { fixString, useNavigate } from "@monta/router-react";
import clsx from "clsx";
import { useEffect, useMemo } from "react";
import TagManager from "react-gtm-module";
import styles from "./cart.module.scss";

const Cart = () => {
  const items = cart.useCartDetailedItems();
  const { showLoading, hideLoading } = useLoading();
  const loginInfo = auth.useAuth();
  const breakLg = useBreakLG();
  const navigate = useNavigate();

  const totalBeforeDiscount = useMemo(() => {
    let totalPrice = 0;
    items?.forEach((item) => {
      totalPrice += item.priceBeforeDiscount * item.count;
    });
    return totalPrice;
  }, [items]);

  const totalAfterDiscount = useMemo(() => {
    let totalPrice = 0;
    items?.forEach((item) => {
      totalPrice += item.priceAfterDiscount * item.count;
    });
    return totalPrice;
  }, [items]);

  const handleLoading = async () => {
    await Promise.all([
      auth.initializing,
      cart.fetchingCart,
      cart.fetchingDetailedCart
    ]);
    hideLoading();
  };

  const validateLogin = () => {
    if (loginInfo) {
      navigate("/cart/payment");
    } else {
      navigate(
        `/auth?redirect=${encodeURIComponent(fixString("/cart/payment", "APPLICATION_URL"))}`
      );
    }
  };

  useEffect(() => {
    showLoading();
    handleLoading();
  }, [handleLoading, showLoading]);

  useEffect(() => {
    TagManager.dataLayer({
      dataLayer: {
        event: "begin_checkout",
        ecommerce: {
          value: totalAfterDiscount,
          items: items?.map((item, index) => {
            return {
              item_id: item.productId,
              item_name: item.productName,
              index,
              price: item.priceAfterDiscount
            };
          })
        }
      }
    });
  }, [items]);

  const renderData = useMemo(() => {
    return (
      <div key={"hasProduct"} className={styles["cart-container"]}>
        <div key={"hasProduct-1"} className={styles.itemsContainer}>
          <CartList items={items} />
        </div>
        <div className={styles.positionFixedContainer}>
          <div className={styles.buySectionContainer}>
            <div className={styles.priceContainer}>
              <div className={styles["priceContainer__title"]}>
                جمع مبلغ سبد خرید:
              </div>
              <div className={styles["priceContainer__price"]}>
                <span className={styles["priceContainer__price__h2"]}>
                  {IRRtoIRT(totalBeforeDiscount).toLocaleString()}
                </span>
                <span className={styles["priceContainer__price__text"]}>
                  تومان
                </span>
              </div>
            </div>
            {items && items.find((item) => item.discountPercent !== 0) && (
              <div className={styles["discount"]}>
                <div className={styles["discount__title"]}>
                  سود شما از خرید:
                </div>
                <div className={styles["discount__price"]}>
                  <span className={styles["discount__price-value"]}>
                    {IRRtoIRT(
                      totalBeforeDiscount - totalAfterDiscount
                    ).toLocaleString()}
                  </span>
                  <span className={styles["discount__price-text"]}>تومان</span>
                </div>
              </div>
            )}
            <Divider color="neutral-100" />
            <div className={styles["finalPrice"]}>
              <div className={styles["finalPrice__title"]}>
                مبلغ قابل پرداخت:
              </div>
              <div className={styles["finalPrice__price"]}>
                <span className={styles["finalPrice__price-value"]}>
                  {IRRtoIRT(totalAfterDiscount).toLocaleString()}
                </span>
                <span className={styles["finalPrice__price-text"]}>تومان</span>
              </div>
            </div>
            <Button
              id={
                loginInfo ? "cart-complete-purchase" : "cart-login-and-continue"
              }
              className={styles["positionFixedContainer__submit"]}
              onClick={validateLogin}
            >
              {loginInfo ? "تکمیل خرید" : "ورود و ادامه"}
            </Button>
          </div>
        </div>
        <div className={styles["cart__submit--mobile"]}>
          <div className={styles["cart__submit--mobile-text"]}>
            <div className={styles["cart__submit--mobile--text"]}>جمع کل:</div>
            <div className={styles["cart__submit--mobile-price"]}>
              {IRRtoIRT(totalAfterDiscount).toLocaleString()} تومان
            </div>
          </div>
          <Button
            id={
              loginInfo ? "cart-complete-purchase" : "cart-login-and-continue"
            }
            className={styles["cart__submit--mobile-button"]}
            onClick={validateLogin}
          >
            {loginInfo ? "تکمیل خرید" : "ورود و ادامه"}
          </Button>
        </div>
      </div>
    );
  }, [items, loginInfo, totalAfterDiscount, totalBeforeDiscount]);

  return (
    <>
      <head>
        <title>سبدخرید | منتا</title>
      </head>
      <div className={clsx(breakLg ? styles.containerBg : "bg-white")}>
        {items?.length ? (
          <div
            className={clsx(
              styles["containerBg__items-container"],
              "container"
            )}
          >
            {!!items.length && (
              <div
                className={clsx(
                  styles["containerBg__items-container__title"],
                  styles.title
                )}
              >
                سبدخرید
              </div>
            )}
            {renderData}
          </div>
        ) : (
          <EmptyView loginInfo={loginInfo} />
        )}
      </div>
    </>
  );
};

export default Cart;
